"use client";
import React, { useState, useRef, useEffect } from 'react';
import styles from "./InputDesign.module.css";
import { UserProfile } from "./UserProfile";
import { CreditsDisplay } from "./CreditsDisplay";
import { PersonalInfoForm } from "./PersonalInfoForm";
import { ChangePasswordSection } from "./ChangePasswordSection";
import { getUserProfile, updatePassword, updateUserProfile } from '../../../dbApiFunctions';
import { PROFILE_USER_AVATAR } from '../../../constant';
import { showSuccessAlert, showErrorAlert } from '../../../utils/sweetAlertConfig';

function InputDesign() {
  const [isEditable, setIsEditable] = useState(false);
  const [showPersonalInfo, setShowPersonalInfo] = useState(true);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [profileImage, setProfileImage] = useState(PROFILE_USER_AVATAR);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [userData, setUserData] = useState(null);

  useEffect(() => {
    const email = sessionStorage.getItem("email");
    const fetchUserData = async () => {
      const userProfile = await getUserProfile(email);
      if (userProfile.status) {
        setUserData(userProfile.user);
        // console.log("User data set successfully:", userProfile.user);
      } else {
        showErrorAlert(userProfile.message, 'Error', 'Please try again');
      }
    };

    if (email) {
      fetchUserData();
    } else {
      console.error("No email found in session storage");
    }
  }, []);

  // Add a second useEffect to update form data when userData changes
  useEffect(() => {
    if (userData) {
      // console.log("Updating form data with userData:", userData);
      setFormData({
        firstName: userData.userName || "",
        lastName: userData.lastName || "",
        email: userData.email || "",
        phone: userData.phone || "",
        oldPassword: "",
        newPassword: "",
        confirmPassword: ""
      });
    }
  }, [userData]);

  // Form state
  const [formData, setFormData] = useState({
    firstName: userData?.userName || "",
    lastName: userData?.lastName || "",
    email: userData?.email || "",
    phone: userData?.phone || "",
    oldPassword: "",
    newPassword: "",
    confirmPassword: ""
  });

  const toggleEditing = () => {
    setIsEditable(!isEditable);
  };

  const handleImageClick = () => {
    // if (isEditable) {
    //     fileInputRef.current.click();
    // }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setIsUploading(true);

      // Create a preview URL for the selected image
      const imageUrl = URL.createObjectURL(file);

      // Simulate upload delay (replace with actual API call)
      setTimeout(() => {
        setProfileImage(imageUrl);
        setIsUploading(false);
        // Here you would typically upload the file to your server
        // and update the profile with the new image URL
      }, 1000);
    }
  };

  const handleInputChange = (e, field) => {
    setFormData({
      ...formData,
      [field]: e.target.value
    });
  };

  const handleLogout = () => {
    // Clear session storage
    sessionStorage.removeItem("username");
    sessionStorage.removeItem("password");

    // Redirect to login page or home page
    window.location.href = "/webapp";
  };

  const handleSaveChanges = async () => {
    const email = sessionStorage.getItem("email");
    const userData = {
      userName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      phone: formData.phone
    };

    const result = await updateUserProfile(email, userData);

    if (result.status) {
      showSuccessAlert(
        'Profile Updated!',
        `
        <div style="text-align: left; margin-top: 1rem;">
            <div style="margin-bottom: 0.5rem;">
                <i class="fas fa-user" style="color: #4CAF50; margin-right: 0.5rem;"></i>
                <strong>First Name:</strong> ${formData.firstName}
            </div>
            <div style="margin-bottom: 0.5rem;">
                <i class="fas fa-user" style="color: #4CAF50; margin-right: 0.5rem;"></i>
                <strong>Last Name:</strong> ${formData.lastName}
            </div>
            <div style="margin-bottom: 0.5rem;">
                <i class="fas fa-envelope" style="color: #4CAF50; margin-right: 0.5rem;"></i>
                <strong>Email:</strong> ${formData.email}
            </div>
            <div style="margin-bottom: 0.5rem;">
                <i class="fas fa-phone" style="color: #4CAF50; margin-right: 0.5rem;"></i>
                <strong>Phone:</strong> ${formData.phone}
            </div>
        </div>
        `,
        'Your profile has been updated successfully'
      );
      setIsEditable(false);
    } else {
      showErrorAlert(result.message, 'Update Failed', 'Please try again');
    }
  };

  const handleResetPassword = async () => {
    if (formData.newPassword !== formData.confirmPassword) {
      showErrorAlert('New password and confirm password do not match!', 'Password Mismatch', 'Please make sure both passwords are identical');
      return;
    }

    const email = sessionStorage.getItem("email");
    const password = formData.newPassword;
    const updatePasswordResult = await updatePassword(email, password);

    if (updatePasswordResult.status) {
      showSuccessAlert(
        'Password Updated!',
        'Your password has been changed successfully.',
        'Please use your new password for future logins'
      );
      setIsEditable(false);
    } else {
      showErrorAlert(updatePasswordResult.message, 'Update Failed', 'Please try again');
    }
  };

  return (
    <main className={styles.container}>
      <aside className={styles.leftColumn}>
        <UserProfile 
          isEditable={isEditable}
          profileImage={profileImage}
          isUploading={isUploading}
          fileInputRef={fileInputRef}
          handleImageClick={handleImageClick}
          handleFileChange={handleFileChange}
          formData={formData}
          handleLogout={handleLogout}
        />
        <CreditsDisplay />
      </aside>
      <div className={styles.rightColumn}>
        <PersonalInfoForm 
          isEditable={isEditable}
          showPersonalInfo={showPersonalInfo}
          setShowPersonalInfo={setShowPersonalInfo}
          formData={formData}
          handleInputChange={handleInputChange}
          handleSaveChanges={handleSaveChanges}
          toggleEditing={toggleEditing}
        />
        <ChangePasswordSection 
          isEditable={isEditable}
          showChangePassword={showChangePassword}
          setShowChangePassword={setShowChangePassword}
          formData={formData}
          handleInputChange={handleInputChange}
          handleResetPassword={handleResetPassword}
        />
      </div>
    </main>
  );
}

export default InputDesign;
